/**
 * DRPX Courier — Bewertungen (Supabase)
 *
 * CRUD für die `bewertungen`-Tabelle (Firma ↔ Fahrer wechselseitig
 * nach Auftragsabschluss).
 *
 * NICHT zu verwechseln mit `fahrer_rechnungen` (eigene Rechnungen der
 * Fahrer an ihre Kunden) — die liegen in `fahrer.ts`.
 *
 * HINWEIS: Diese Datei hieß ursprünglich `rechnungen.ts` und enthielt
 * auch CRUD für Plattform-Rechnungen (`rechnungen`-Tabelle). Diese
 * wurde am 17.05.2026 entfernt (Migration 078), weil Stripe sämtliche
 * Abo-Rechnungen automatisch generiert. Datei-Name aus Kompatibilitäts-
 * gründen unverändert — Re-Export-Kette bleibt intakt.
 *
 * @module lib/supabase/rechnungen
 */

import { _sb } from './client.js';
import { mapBewertungFromDb } from './mappers.js';
import { todayLocal } from '../format.js';
import type { Bewertung, BewertungTyp } from '../../types.js';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

// ════════════════════════════════════════════════════════════════════
// Lokale Input-Interfaces (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

/**
 * Input-Shape für `createBewertung`.
 *
 * `typ` nutzt die zentrale {@link BewertungTyp}-Union aus `types.ts` —
 * siehe dort die Dokumentation zur bestehenden `_transportunternehmer`/`_fahrer`
 * Benennungs-Inkonsistenz.
 */
interface BewertungCreateInput {
  typ: BewertungTyp;
  vonId: string;
  vonName: string;
  fuerId: string;
  fuerName: string;
  auftragId: string;
  sterne: number;
  attribute?: Record<string, number> | null;
  datum?: string;
  kommentar?: string;
}

// ════════════════════════════════════════════════════════════════════
// BEWERTUNGEN
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt die letzten 500 Bewertungen der Plattform (Firma ↔ Fahrer
 * wechselseitig nach Auftragsabschluss).
 */
export async function getBewertungen(): Promise<{
  data: Bewertung[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('bewertungen')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500);
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapBewertungFromDb(row))
      .filter((row): row is Bewertung => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Legt eine neue Bewertung an (1–5 Sterne + optionaler Kommentar).
 */
export async function createBewertung(
  b: BewertungCreateInput,
): Promise<{ data: Bewertung | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      typ: b.typ,
      von_id: b.vonId,
      von_name: b.vonName,
      fuer_id: b.fuerId,
      fuer_name: b.fuerName,
      auftrag_id: b.auftragId,
      sterne: b.sterne,
      attribute: b.attribute || null,
      datum: b.datum || todayLocal(),
      kommentar: b.kommentar || '',
    };
    const r = await client.from('bewertungen').insert(d).select().single();
    // Migration-095-Stil (Vorbild auftraege.ts): Constraint-Fehler in
    // strukturierte ApiError mit i18n-Key übersetzen, damit das Frontend eine
    // freundliche Meldung zeigen kann statt eines rohen Postgres-Fehlertexts.
    if (r.error) {
      const pgCode = (r.error as { code?: string }).code;
      const pgMsg = (r.error as { message?: string }).message ?? '';
      // 23505 = unique_violation → UNIQUE(auftrag_id, von_id). Tritt auf, wenn
      // jemand denselben Auftrag doppelt bewertet (2 Tabs / schneller
      // Doppelklick, bevor die Frontend-Prüfung greift).
      if (pgCode === '23505' && pgMsg.includes('bewertungen_unique_von_auftrag')) {
        return { data: null, error: { code: 'hcBereitsBewertetText' } };
      }
      return { data: null, error: r.error };
    }
    return {
      data: r.data ? mapBewertungFromDb(r.data as Record<string, unknown>) : null,
      error: null,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Löscht eine Bewertung unwiderruflich. Normalerweise nur für Admin-
 * Moderationszwecke (z.B. bei beleidigendem Kommentar).
 */
export async function deleteBewertung(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('bewertungen').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}
